import { Injectable } from '@angular/core';
import { AuctionItem } from '../../loyalty/auction/models/auction-item.model';
import { DateTime } from 'luxon';
import { MarketplaceItemExtended } from '../../loyalty/marketplace/models/marketplace-item-extended.model';
import { MarketplaceItemStatus } from '../../loyalty/marketplace/models/marketplace-item-status';
import { MemberQuery } from '../../account/member/member.query';
import { BalanceQuery } from '../../account/balance/balance.query';
import { ItemLoyaltyState } from '../lotto/models/item-loyalty-state';
import { LottoItem } from '../lotto/models/lotto-item.model';
import { MarketplaceState } from './models/marketplace-state.model';
import { LOYALTY_STATUS } from './models/loyalty-status-type.enum';
import { AuctionState } from './models/auction-state.model';
import { LoyaltyStatus } from './models/loyalty-status.model';
import { LottoState } from './models/lotto-state.model';

@Injectable({
  providedIn: 'root',
})
export class LoyaltyStatusService {
  /**
   * These are statuses that are considered stock statuses
   * ex. limited stock, stock is low like 3 left.etc
   */
  stockStatus = [LOYALTY_STATUS.LIMITED_STOCK, LOYALTY_STATUS.STOCK_LOW];
  /**
   * These are statuses that are considered disabled
   */
  disabledStatus = [
    LOYALTY_STATUS.SOLD_OUT,
    LOYALTY_STATUS.COMING_SOON,
    LOYALTY_STATUS.LIMIT_REACHED,
    LOYALTY_STATUS.NOT_ENOUGH_POINTS,
  ];

  constructor(
    private memberQuery: MemberQuery,
    private balanceQuery: BalanceQuery
  ) {}

  /**
   * Gets the status of an item
   */
  getStatus(item: any): LoyaltyStatus {
    // Check the instance of the item then get the status
    if (item instanceof AuctionItem) {
      return this._getAuctionStatus(item);
    } else if (item instanceof MarketplaceItemExtended) {
      return this._getMarketplaceStatus(item);
    } else if (item instanceof LottoItem) {
      return this._getLottoStatus(item);
    }
    return new LoyaltyStatus();
  }

  /**
   * Check if the item has a disabled state
   */
  hasDisabledStatus(item: any): boolean {
    const status = this.getStatus(item);
    return this.disabledStatus.some((s) => s === status.status);
  }

  /**
   * Check if the item has a stock status ex. limited stock, stock low
   */
  hasStockStatus(item: any): boolean {
    const status = this.getStatus(item);
    return this.stockStatus.some((s) => s === status.status);
  }

  /**
   * Check if the item is new where item creation date is less than 3 days
   */
  isNew(item: any): boolean {
    const status = this.getStatus(item);
    return status.status === LOYALTY_STATUS.NEW;
  }

  /**
   * Get the status of an auction item
   * @returns LoyaltyStatus
   */
  private _getAuctionStatus(item: any): LoyaltyStatus {
    const memberId = this.memberQuery.getValue().user?.memberId;
    const balance = this.balanceQuery.getEntity([memberId, 6].join('-'))?.balance || 0;

    const requestTime = DateTime.fromISO(item.RequestTime as string, { zone: 'utc' });
    const startDate = DateTime.fromISO(item.StartDate as string, { zone: 'utc' });
    const pointsValue = item.PointsValue ?? 0;
    const bidIncrement = item.BidIncrement ?? 0;
    const currentValue = item.CurrentValue ?? 0;
    let comingSoon = item.ComingSoon;

    if (startDate <= requestTime) {
      comingSoon = false;
    }

    if (comingSoon && startDate > requestTime) {
      return new AuctionState({ theme: 'coming-soon', status: LOYALTY_STATUS.COMING_SOON });
    } else if (pointsValue > balance || bidIncrement > balance || currentValue + bidIncrement > balance) {
      return new AuctionState({ theme: 'not-enough-points', status: LOYALTY_STATUS.NOT_ENOUGH_POINTS });
    } else {
      const days = requestTime.diff(startDate, 'days').days;

      const status = days <= 3 ? LOYALTY_STATUS.NEW : item.Favourite ? LOYALTY_STATUS.FEATURED : LOYALTY_STATUS.NONE;
      return new AuctionState({ status });
    }
  }

  /**
   * Get the status of a marketplace item
   * @param item
   * @returns LoyaltyStatus
   */
  private _getMarketplaceStatus(item: MarketplaceItemExtended): LoyaltyStatus {
    if (item.Locked) {
      return new MarketplaceState({ theme: 'item-locked', status: LOYALTY_STATUS.LOCKED });
    }

    const availableStock = item.AvailableCount || 0;
    const isUnlimited = !!item.UnlimitedStock;
    let comingSoon = item.ComingSoon;
    const requestTime = DateTime.fromISO(item.RequestTime as string, { zone: 'utc' });
    const startDate = DateTime.fromISO(item.ItemStartDate as string, { zone: 'utc' });

    if (startDate <= requestTime) {
      comingSoon = false;
    }

    if (comingSoon && startDate > requestTime) {
      return new MarketplaceState({ theme: 'coming-soon', status: LOYALTY_STATUS.COMING_SOON });
    } else if (!isUnlimited && availableStock <= 0) {
      return new MarketplaceState({ theme: 'sold-out', status: LOYALTY_STATUS.SOLD_OUT });
    } else if (item.Status === MarketplaceItemStatus.NotEnoughPoints) {
      return new MarketplaceState({ theme: 'not-enough-points', status: LOYALTY_STATUS.NOT_ENOUGH_POINTS });
    } else if (item.Status === MarketplaceItemStatus.MemberLimitReached) {
      return new MarketplaceState({ theme: 'limit-reached', status: LOYALTY_STATUS.LIMIT_REACHED });
    } else {
      const stockCount = item.AvailabilityCount;
      // Limited stock
      if (stockCount > 5 && stockCount <= 10) {
        return new MarketplaceState({ theme: 'limited-item', status: LOYALTY_STATUS.LIMITED_STOCK });
      } else if (stockCount > 0 && stockCount <= 5) {
        return new MarketplaceState({ theme: 'stock-low', status: LOYALTY_STATUS.STOCK_LOW });
      } else {
        const days = requestTime.diff(startDate, 'days').days;

        const status = days <= 3 ? LOYALTY_STATUS.NEW : item.Favourite ? LOYALTY_STATUS.FEATURED : LOYALTY_STATUS.NONE;

        return new MarketplaceState({ status });
      }
    }
  }

  /**
   * Get the status of a lotto item
   * @param item
   * @returns LoyaltyStatus
   */
  private _getLottoStatus(item: LottoItem): LoyaltyStatus {
    if (item.Locked) {
      return new MarketplaceState({ theme: 'item-locked', status: LOYALTY_STATUS.LOCKED });
    }

    const memberId = this.memberQuery.getValue().user?.memberId;
    const balance = this.balanceQuery.getEntity([memberId, 6].join('-'))?.balance || 0;

    const requestTime = DateTime.fromISO(item.RequestTime as string, { zone: 'utc' });
    const startDate = DateTime.fromISO(item?.StartDate as string, { zone: 'utc' });
    const purchasePrice = item.PurchasePrice ?? 0;
    let comingSoon = item.AllowComingSoon === ItemLoyaltyState.ComingSoon;

    if (startDate <= requestTime) {
      comingSoon = false;
    }

    if (comingSoon && startDate > requestTime) {
      return new LottoState({ theme: 'coming-soon', status: LOYALTY_STATUS.COMING_SOON });
    } else if (item.ItemState === ItemLoyaltyState.OutOfStock) {
      return new LottoState({ theme: 'sold-out', status: LOYALTY_STATUS.SOLD_OUT });
    } else if (item.ItemState === ItemLoyaltyState.ItemLimitReached) {
      return new LottoState({ theme: 'limit-reached', status: LOYALTY_STATUS.LIMIT_REACHED });
    } else {
      if (balance < purchasePrice) {
        return new LottoState({ theme: 'not-enough-points', status: LOYALTY_STATUS.NOT_ENOUGH_POINTS });
      } else {
        const days = requestTime.diff(startDate, 'days').days;
        const status = days <= 3 ? LOYALTY_STATUS.NEW : item.Favourite ? LOYALTY_STATUS.FEATURED : LOYALTY_STATUS.NONE;

        return new LottoState({ status });
      }
    }
  }
}
